import { useState, useEffect, useCallback, useMemo } from 'react'
import { BlogMetadataRecord } from 'api/clients/BlogMetadataRecordsClient'
import {
  GoHighLevelProfile,
  WixProfile,
  WordPressProfile,
  ExternalIntegrationEnum,
  ExternalIntegration,
  ShopifyProfile,
} from 'api/clients/ExternalIntegrationsClient'

export interface BlogMetadataIntegrationState {
  selectedBlogId?: string
  selectedAuthorId?: string
  isGoHighLevelIntegration: boolean
  isWixIntegration: boolean
  isShopifyIntegration: boolean
  isWordPressIntegration: boolean
  availableBlogs: Array<{ id: string; name: string }>
  availableAuthors: Array<{ id: string; name: string }>
  selectedIntegration?: ExternalIntegration | null
  availableCategories: string[]
  allowAddingCategories: boolean
  addCategoryDisabledHint?: string
  useCategoryDropdown: boolean
}

export interface BlogMetadataIntegrationHandlers {
  handleBlogSelect: (blogId: string) => void
  handleAuthorSelect: (authorId: string) => void
}

export interface UseBlogMetadataIntegrationOptions {
  initialData?: BlogMetadataRecord
  selectedIntegration?: ExternalIntegration | null
  wixEnabled: boolean
  shopifyEnabled: boolean
  onIntegrationUpdate?: (updates: Partial<BlogMetadataRecord>) => void
}

export interface UseBlogMetadataIntegrationReturn {
  integrationState: BlogMetadataIntegrationState
  handlers: BlogMetadataIntegrationHandlers
}

export const useBlogMetadataIntegration = ({
  initialData,
  selectedIntegration,
  wixEnabled,
  onIntegrationUpdate,
  shopifyEnabled,
}: UseBlogMetadataIntegrationOptions): UseBlogMetadataIntegrationReturn => {
  // GoHighLevel and Wix blog and author selection state
  const [selectedBlogId, setSelectedBlogId] = useState<string | undefined>(initialData?.selected_blog_id)
  const [selectedAuthorId, setSelectedAuthorId] = useState<string | undefined>(initialData?.selected_author_id)

  // Determine if this is a GoHighLevel integration
  const isGoHighLevelIntegration = selectedIntegration?.provider === ExternalIntegrationEnum.GoHighLevel

  // Determine if this is a Wix integration
  const isWixIntegration = selectedIntegration?.provider === ExternalIntegrationEnum.Wix && wixEnabled

  // Determine if this is a Shopify integration
  const isShopifyIntegration = selectedIntegration?.provider === ExternalIntegrationEnum.Shopify && shopifyEnabled

  // Determine if this is a WordPress integration
  const isWordPressIntegration = selectedIntegration?.provider === ExternalIntegrationEnum.WordPress

  // Get GoHighLevel profile data
  const ghlProfile = isGoHighLevelIntegration ? (selectedIntegration?.additional_data as GoHighLevelProfile) : null

  // Get Wix profile data
  const wixProfile = isWixIntegration ? (selectedIntegration?.additional_data as WixProfile) : null

  // Get Shopify profile data
  const shopifyProfile = isShopifyIntegration ? (selectedIntegration?.additional_data as ShopifyProfile) : null

  // Get WordPress profile data
  const wpProfile = isWordPressIntegration ? (selectedIntegration?.additional_data as WordPressProfile) : null

  // For GoHighLevel, extract blogs and authors from the profile
  // For Wix, extract members from the profile
  // For Shopify, extract blogs and authors from the Shopify profile
  // For WordPress, extract users from the profile
  let availableBlogs: Array<{ id: string; name: string }> = []
  let availableAuthors: Array<{ id: string; name: string }> = []

  if (isGoHighLevelIntegration) {
    availableBlogs = ghlProfile?.blogs ?? []
    availableAuthors = ghlProfile?.authors ?? []
  } else if (isWixIntegration) {
    availableAuthors = wixProfile?.wix_members ?? []
  } else if (isShopifyIntegration) {
    availableBlogs = shopifyProfile?.shopify_blogs ?? []
    availableAuthors = shopifyProfile?.shopify_authors ?? []
  } else if (isWordPressIntegration) {
    // Convert WordPress users to the expected format (id as string)
    availableAuthors = (wpProfile?.wordpress_users ?? []).map((user) => ({
      id: String(user.id),
      name: user.name,
    }))
  }

  // Sync state with initialData changes
  useEffect(() => {
    setSelectedBlogId(initialData?.selected_blog_id)
  }, [initialData?.selected_blog_id])

  useEffect(() => {
    setSelectedAuthorId(initialData?.selected_author_id)
  }, [initialData?.selected_author_id])

  // Backfill blog and author IDs when GoHighLevel integration is added or changes
  useEffect(() => {
    // Only proceed if this is a GoHighLevel integration and the feature is enabled
    if (!selectedIntegration || selectedIntegration.provider !== ExternalIntegrationEnum.GoHighLevel) {
      return
    }

    const ghlProfile = selectedIntegration.additional_data as GoHighLevelProfile
    if (!ghlProfile) {
      return
    }

    let shouldUpdateMetadata = false
    const updates: Partial<BlogMetadataRecord> = {}

    // Check if we need to backfill selected_blog_id
    if (!selectedBlogId && ghlProfile.selected_blog_id) {
      // Use the integration's default blog ID
      setSelectedBlogId(ghlProfile.selected_blog_id)
      updates.selected_blog_id = ghlProfile.selected_blog_id
      shouldUpdateMetadata = true
    } else if (!selectedBlogId && ghlProfile.blogs && ghlProfile.blogs.length === 1) {
      // Auto-select if only one blog is available
      const singleBlogId = ghlProfile.blogs[0].id
      setSelectedBlogId(singleBlogId)
      updates.selected_blog_id = singleBlogId
      shouldUpdateMetadata = true
    }

    // Check if we need to backfill selected_author_id
    if (!selectedAuthorId && ghlProfile.selected_author_id) {
      // Use the integration's default author ID
      setSelectedAuthorId(ghlProfile.selected_author_id)
      updates.selected_author_id = ghlProfile.selected_author_id
      shouldUpdateMetadata = true
    } else if (!selectedAuthorId && ghlProfile.authors && ghlProfile.authors.length === 1) {
      // Auto-select if only one author is available
      const singleAuthorId = ghlProfile.authors[0].id
      setSelectedAuthorId(singleAuthorId)
      updates.selected_author_id = singleAuthorId
      shouldUpdateMetadata = true
    }

    // Update the metadata record if we made changes
    if (shouldUpdateMetadata) {
      onIntegrationUpdate?.(updates)
    }
  }, [selectedIntegration, selectedBlogId, selectedAuthorId, onIntegrationUpdate])

  // Backfill author ID when Wix integration is added or changes
  useEffect(() => {
    // Only proceed if this is a Wix integration and the feature is enabled
    if (!selectedIntegration || selectedIntegration.provider !== ExternalIntegrationEnum.Wix || !wixEnabled) {
      return
    }

    const wixProfile = selectedIntegration.additional_data as WixProfile
    if (!wixProfile) {
      return
    }

    let shouldUpdateMetadata = false
    const updates: Partial<BlogMetadataRecord> = {}

    // Check if we need to backfill selected_author_id
    if (!selectedAuthorId && wixProfile.selected_author_id) {
      // Use the integration's default author ID
      setSelectedAuthorId(wixProfile.selected_author_id)
      updates.selected_author_id = wixProfile.selected_author_id
      shouldUpdateMetadata = true
    } else if (!selectedAuthorId && wixProfile.wix_members && wixProfile.wix_members.length === 1) {
      // Auto-select if only one member is available
      const singleMemberId = wixProfile.wix_members[0].id
      setSelectedAuthorId(singleMemberId)
      updates.selected_author_id = singleMemberId
      shouldUpdateMetadata = true
    }

    // Update the metadata record if we made changes
    if (shouldUpdateMetadata) {
      onIntegrationUpdate?.(updates)
    }
  }, [selectedIntegration, wixEnabled, selectedAuthorId, onIntegrationUpdate])

  // Note: Wix author backfill logic is handled in the useEffect above

  // Backfill blog and author IDs when Shopify integration is added or changes
  useEffect(() => {
    // Only proceed if this is a Shopify integration and the feature is enabled
    if (!selectedIntegration || selectedIntegration.provider !== ExternalIntegrationEnum.Shopify || !shopifyEnabled) {
      return
    }

    const shopifyProfile = selectedIntegration.additional_data as ShopifyProfile
    if (!shopifyProfile) {
      return
    }

    let shouldUpdateMetadata = false
    const updates: Partial<BlogMetadataRecord> = {}

    // Check if we need to backfill selected_blog_id
    if (!selectedBlogId && shopifyProfile.selected_blog_id) {
      // Use the integration's default blog ID
      setSelectedBlogId(shopifyProfile.selected_blog_id)
      updates.selected_blog_id = shopifyProfile.selected_blog_id
      shouldUpdateMetadata = true
    } else if (!selectedBlogId && shopifyProfile.shopify_blogs && shopifyProfile.shopify_blogs.length === 1) {
      // Auto-select if only one blog is available
      const singleBlogId = shopifyProfile.shopify_blogs[0].id
      setSelectedBlogId(singleBlogId)
      updates.selected_blog_id = singleBlogId
      shouldUpdateMetadata = true
    }

    // Check if we need to backfill selected_author_id
    if (!selectedAuthorId && shopifyProfile.selected_author_id) {
      // Use the integration's default author ID
      setSelectedAuthorId(shopifyProfile.selected_author_id)
      updates.selected_author_id = shopifyProfile.selected_author_id
      shouldUpdateMetadata = true
    } else if (!selectedAuthorId && shopifyProfile.shopify_authors && shopifyProfile.shopify_authors.length === 1) {
      // Auto-select if only one author is available
      const singleAuthorId = shopifyProfile.shopify_authors[0].id
      setSelectedAuthorId(singleAuthorId)
      updates.selected_author_id = singleAuthorId
      shouldUpdateMetadata = true
    }

    // Update the metadata record if we made changes
    if (shouldUpdateMetadata) {
      onIntegrationUpdate?.(updates)
    }
  }, [selectedIntegration, shopifyEnabled, selectedBlogId, selectedAuthorId, onIntegrationUpdate])

  // Backfill author ID when WordPress integration is added or changes
  useEffect(() => {
    // Only proceed if this is a WordPress integration
    if (!selectedIntegration || selectedIntegration.provider !== ExternalIntegrationEnum.WordPress) {
      return
    }

    const wpProfile = selectedIntegration.additional_data as WordPressProfile
    if (!wpProfile || !wpProfile.wordpress_users || wpProfile.wordpress_users.length === 0) {
      return
    }

    // If author is already selected, don't override
    if (selectedAuthorId) {
      return
    }

    let authorIdToSelect: string | undefined

    // Priority 1: Use the selected_author_id from integration settings
    if (wpProfile.selected_author_id) {
      const defaultAuthor = wpProfile.wordpress_users.find((user) => user.id === wpProfile.selected_author_id)
      if (defaultAuthor) {
        authorIdToSelect = String(defaultAuthor.id)
      }
    }

    // Priority 2: Auto-select if only one user is available
    if (!authorIdToSelect && wpProfile.wordpress_users.length === 1) {
      authorIdToSelect = String(wpProfile.wordpress_users[0].id)
    }

    // Update if we found an author to select
    if (authorIdToSelect) {
      setSelectedAuthorId(authorIdToSelect)
      onIntegrationUpdate?.({ selected_author_id: authorIdToSelect })
    }
  }, [selectedIntegration, selectedAuthorId, onIntegrationUpdate])

  // Extract available categories based on integration type
  const availableCategories = useMemo(() => {
    if (!selectedIntegration) return []

    let platformCategories: string[] = []

    if (selectedIntegration.provider === ExternalIntegrationEnum.WordPress) {
      const wpProfile = selectedIntegration.additional_data as WordPressProfile
      platformCategories = wpProfile?.wordpress_categories?.map((cat) => cat.name) ?? []
    } else if (isGoHighLevelIntegration) {
      platformCategories = ghlProfile?.go_high_level_categories?.map((cat) => cat.name) ?? []
    }

    // Add previously selected custom categories
    const previouslySelected = initialData?.categories ?? []
    const customCategories = previouslySelected.filter(
      (selected) => !platformCategories.some((cat) => cat.toLowerCase() === selected.toLowerCase()),
    )

    // Combine and deduplicate
    const finalCategories =
      platformCategories.length === 0
        ? ['Uncategorized', ...customCategories]
        : [...platformCategories, ...customCategories]

    return finalCategories.filter(
      (cat, idx, arr) => arr.findIndex((c) => c.toLowerCase() === cat.toLowerCase()) === idx,
    )
  }, [selectedIntegration, isGoHighLevelIntegration, ghlProfile, initialData?.categories])

  // Determine permissions and UI style.
  // For WordPress, we only block adding new categories when the connected user
  // is *confirmed* to lack the `manage_categories` capability — if the probe
  // was skipped or failed (field missing), we still allow adding to avoid
  // false negatives for users who actually have permission.
  const wpCanManageCategories = isWordPressIntegration ? wpProfile?.can_manage_categories : undefined
  const allowAddingCategories = isWordPressIntegration && wpCanManageCategories !== false
  const addCategoryDisabledHint =
    isWordPressIntegration && wpCanManageCategories === false
      ? "Your WordPress account doesn't have permission to create new categories. Ask your WordPress admin for Editor access, or create the category in WordPress first."
      : undefined
  const useCategoryDropdown = !isWixIntegration

  // Handlers
  const handleBlogSelect = useCallback(
    (blogId: string) => {
      setSelectedBlogId(blogId)
      onIntegrationUpdate?.({ selected_blog_id: blogId })
    },
    [onIntegrationUpdate],
  )

  const handleAuthorSelect = useCallback(
    (authorId: string) => {
      setSelectedAuthorId(authorId)
      onIntegrationUpdate?.({ selected_author_id: authorId })
    },
    [onIntegrationUpdate],
  )

  return {
    integrationState: {
      selectedBlogId,
      selectedAuthorId,
      isGoHighLevelIntegration,
      isWixIntegration,
      isWordPressIntegration,
      availableBlogs,
      availableAuthors,
      selectedIntegration,
      availableCategories,
      allowAddingCategories,
      addCategoryDisabledHint,
      useCategoryDropdown,
      isShopifyIntegration,
    },
    handlers: {
      handleBlogSelect,
      handleAuthorSelect,
    },
  }
}
